/**
 * NetworkNodeFormModal — Sprint 4.2C.
 *
 * Modal para criar/editar POP/OLT/CTO/Splitter/Other.
 *   - clickedLatLng: opcional {lat,lng} preenchidos por clique no mapa
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { createNetworkNode, updateNetworkNode } from "../../lib/api.js";

const TYPES = [
  { id: "POP",      label: "POP",      icon: "📡" },
  { id: "OLT",      label: "OLT",      icon: "🗄️" },
  { id: "CTO",      label: "CTO",      icon: "📦" },
  { id: "SPLITTER", label: "Splitter", icon: "🔀" },
  { id: "OTHER",    label: "Other",    icon: "⬢" },
];

export default function NetworkNodeFormModal({
  open, mode = "create", initial = null, clickedLatLng = null,
  onClose, onSaved,
}) {
  const [type, setType] = useState("OLT");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setType(initial.type || "OLT");
      setName(initial.name || "");
      setCode(initial.code || "");
      setLat(initial.lat != null ? String(initial.lat) : "");
      setLng(initial.lng != null ? String(initial.lng) : "");
      setAddress(initial.address || "");
      setDescription(initial.description || "");
    } else {
      setType("OLT");
      setName("");
      setCode("");
      setLat(clickedLatLng?.lat != null ? String(clickedLatLng.lat) : "");
      setLng(clickedLatLng?.lng != null ? String(clickedLatLng.lng) : "");
      setAddress("");
      setDescription("");
    }
    setBusy(false);
    setError(null);
  }, [open, mode, initial, clickedLatLng]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const handleSave = async () => {
    if (!name.trim()) { setError("Nome obrigatório."); return; }
    const latN = lat === "" ? null : Number(lat);
    const lngN = lng === "" ? null : Number(lng);
    if (lat !== "" && Number.isNaN(latN)) { setError("lat inválido"); return; }
    if (lng !== "" && Number.isNaN(lngN)) { setError("lng inválido"); return; }

    setBusy(true);
    setError(null);
    try {
      const body = {
        type, name: name.trim(),
        code: code.trim() || null,
        lat: latN, lng: lngN,
        address: address.trim() || null,
        description: description.trim() || null,
      };
      const result = (mode === "edit" && initial?.id)
        ? await updateNetworkNode(initial.id, body)
        : await createNetworkNode(body);
      onSaved?.(result);
    } catch (e) {
      setError(e?.message || "Erro ao salvar node");
    } finally {
      setBusy(false);
    }
  };

  // Robustez/simetria (MAP-UX): portaliza p/ document.body como
  // ConfirmCriticalAction/BulkRelayRebootModal — imune a transform/filter futuro
  // em .ipwd-fleet-focus (mapa fullscreen). z-[2000] segue acima do mapa e abaixo
  // da confirmação crítica (z-3000/4000).
  return createPortal(
    <div className="fixed inset-0 z-[2000] bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="bg-noc-panel border border-noc-accent/30 rounded shadow-2xl w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-noc-accent">
            {mode === "edit" ? "✎ Editar Infra" : "+ Nova Infra ISP"}
          </h3>
          <button type="button" onClick={onClose} disabled={busy}
            className="text-noc-muted hover:text-noc-text text-lg leading-none">×</button>
        </div>

        <div className="text-xs space-y-1">
          <span className="text-noc-muted">Tipo</span>
          <div className="grid grid-cols-5 gap-1">
            {TYPES.map((t) => (
              <button key={t.id} type="button" onClick={() => setType(t.id)}
                className={`px-2 py-1.5 rounded border text-[10px] ${
                  type === t.id
                    ? "border-noc-accent/30 bg-noc-accent/10 text-noc-accent"
                    : "border-noc-border text-noc-muted hover:text-noc-text"
                }`}>
                <div className="text-base leading-none">{t.icon}</div>
                <div>{t.label}</div>
              </button>
            ))}
          </div>
        </div>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Nome</span>
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder={`ex: ${type}-Centro-01`}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1" />
        </label>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Código (opcional)</span>
          <input value={code} onChange={(e) => setCode(e.target.value)}
            placeholder={`ex: ${type}-CSC-01`}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs space-y-1">
            <span className="text-noc-muted">Latitude</span>
            <input value={lat} onChange={(e) => setLat(e.target.value)}
              placeholder="-10.6822" inputMode="decimal"
              className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
          </label>
          <label className="block text-xs space-y-1">
            <span className="text-noc-muted">Longitude</span>
            <input value={lng} onChange={(e) => setLng(e.target.value)}
              placeholder="-39.4914" inputMode="decimal"
              className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
          </label>
        </div>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Endereço (opcional)</span>
          <input value={address} onChange={(e) => setAddress(e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1" />
        </label>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Descrição (opcional)</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1" />
        </label>

        {error && (
          <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={busy}
            className="text-xs px-3 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text">
            Cancelar
          </button>
          <button type="button" onClick={handleSave} disabled={busy}
            className="text-xs px-3 py-1 rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold disabled:opacity-50">
            {busy ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
